/**
 * Detection / Decisions - Shadow AI (Bank I-162, I-165, III-71).
 *
 * Live data sources:
 *   GET /v1/detection/shadow-ai/usage            → who used which AI service, how much, when
 *   GET /v1/detection/shadow-ai/services         → the AI service list with its approved flag
 *   PUT /v1/detection/shadow-ai/services/:key    → approve or unapprove a service
 *
 * An unapproved service's use is a DDR decision (trigger "shadow ai") in the
 * list above; its Request ID is shown here next to the user. Approved use is
 * recorded and raises nothing.
 *
 * Boundary controls (Bank I-162): ShadowAiBoundaryCard. A service set to
 * block is refused by the customer's proxy; its refused requests are counted
 * here and raise nothing, a request the proxy did not refuse raises a decision.
 */

import { useEffect, useState } from 'react';
import { Bot, Loader2 } from 'lucide-react';

import { GlassCard, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/primitives';
import {
  ApiError,
  listAiServices,
  listShadowAiUsage,
  setAiServiceApproved,
  type AiService,
  type ShadowAiUsage,
} from '@/lib/api';
import { ShadowAiBoundaryCard } from './ShadowAiBoundaryCard';

type UsageFilter = 'unapproved' | 'approved' | 'all';

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v.toFixed(1)} ${units[u]}`;
}

function isUnitLimitedRefusal(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 403) return false;
  const type = (err.body as { type?: unknown } | null)?.type;
  return typeof type === 'string' && type.endsWith('/shadow_ai_needs_all_units');
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { hour12: false });
}

export function ShadowAiPanel({ canApprove, onOpenDecision }: { canApprove: boolean; onOpenDecision?: (requestId: string) => void }) {
  const [filter, setFilter] = useState<UsageFilter>('unapproved');
  const [usage, setUsage] = useState<ShadowAiUsage[] | null>(null);
  const [services, setServices] = useState<AiService[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  // Shadow AI data carries no business unit, so the API refuses a session
  // limited to some units (403, problem type shadow_ai_needs_all_units).
  const [unitLimited, setUnitLimited] = useState(false);
  const onLoadError = (err: unknown) => {
    if (isUnitLimitedRefusal(err)) setUnitLimited(true);
    else setError(err instanceof Error ? err.message : 'load failed');
  };

  useEffect(() => {
    let live = true;
    setUsage(null);
    listShadowAiUsage({ approved: filter === 'all' ? undefined : filter === 'approved', limit: 200 })
      .then((r) => live && setUsage(r.usage))
      .catch((err) => live && onLoadError(err));
    return () => {
      live = false;
    };
  }, [filter]);

  useEffect(() => {
    let live = true;
    listAiServices()
      .then((r) => live && setServices(r.services))
      .catch((err) => live && onLoadError(err));
    return () => {
      live = false;
    };
  }, []);

  async function toggle(svc: AiService) {
    setBusyKey(svc.key);
    setError(null);
    try {
      const { service } = await setAiServiceApproved(svc.key, !svc.approved);
      setServices((cur) => (cur ?? []).map((s) => (s.key === service.key ? service : s)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'change failed');
    } finally {
      setBusyKey(null);
    }
  }

  if (unitLimited) {
    return (
      <div className="mt-8 space-y-4" data-testid="shadow-ai-panel">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-base font-semibold">Shadow AI</h2>
        </div>
        <GlassCard className="p-4">
          <CardDescription className="text-sm" data-testid="shadow-ai-unit-limited">
            Shadow AI use is read from web proxy logs that span every business unit, so it is open to people who see all units. Your access is limited to some
            units.
          </CardDescription>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-4" data-testid="shadow-ai-panel">
      <div className="flex items-center gap-2">
        <Bot className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-base font-semibold">Shadow AI</h2>
        <span className="text-xs text-muted-foreground">AI services seen in web proxy logs. Unapproved use raises a decision above.</span>
      </div>
      {error && (
        <div className="glass-lite rounded-md p-2 text-xs text-sev-critical" role="alert">
          {error}
        </div>
      )}

      <GlassCard className="p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-border/40">
          <CardTitle className="text-sm">Use by person and tool</CardTitle>
          <Tabs value={filter} onValueChange={(v) => setFilter(v as UsageFilter)}>
            <TabsList>
              <TabsTrigger value="unapproved">Unapproved</TabsTrigger>
              <TabsTrigger value="approved">Approved</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {usage === null ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
            Loading
          </div>
        ) : usage.length === 0 ? (
          <CardDescription className="p-6 text-center text-sm">
            No AI service use recorded for this filter. Use appears once a proxy log is sent to POST /v1/detection/shadow-ai/proxy-logs.
          </CardDescription>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
                <th className="text-left px-4 py-2 font-medium">User</th>
                <th className="text-left px-2 py-2 font-medium">Tool</th>
                <th className="text-left px-2 py-2 font-medium">Status</th>
                <th className="text-right px-2 py-2 font-medium">Requests</th>
                <th className="text-right px-2 py-2 font-medium">Sent</th>
                <th className="text-right px-2 py-2 font-medium">Received</th>
                <th className="text-left px-2 py-2 font-medium">First seen</th>
                <th className="text-left px-2 py-2 font-medium">Last seen</th>
                <th className="text-left px-4 py-2 font-medium">Decision</th>
              </tr>
            </thead>
            <tbody>
              {usage.map((u) => (
                <tr key={u.id} className="border-b border-border/30" data-testid="shadow-ai-usage-row">
                  <td className="px-4 py-2 font-mono">{u.user}</td>
                  <td className="px-2 py-2">{u.serviceName}</td>
                  <td className="px-2 py-2">
                    <Badge variant={u.approved ? 'success' : 'critical'} className="text-[9px]">
                      {u.approved ? 'approved' : u.blockedByRule ? 'blocked' : 'unapproved'}
                    </Badge>
                    {(u.refusedCount ?? 0) > 0 && (
                      <span className="ml-1 text-[10px] text-muted-foreground" data-testid="shadow-ai-refused">
                        {u.refusedCount} refused by proxy
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">{u.requestCount}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{formatBytes(u.bytesOut)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{formatBytes(u.bytesIn)}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{when(u.firstSeenAt)}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{when(u.lastSeenAt)}</td>
                  <td className="px-4 py-2">
                    {u.requestId ? (
                      <button
                        type="button"
                        className="font-mono text-[11px] underline decoration-dotted"
                        onClick={() => onOpenDecision?.(u.requestId!)}
                      >
                        {u.requestId}
                      </button>
                    ) : (
                      <span className="text-muted-foreground">recorded, no alert</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </GlassCard>

      <GlassCard className="p-0 overflow-hidden">
        <div className="px-4 py-2 border-b border-border/40">
          <CardTitle className="text-sm">AI services</CardTitle>
          <CardDescription className="text-xs">
            Every service starts unapproved. {canApprove ? 'Approve the tools the bank allows; their use is recorded without an alert.' : 'The DPO or Legal Reviewer approves tools.'}
          </CardDescription>
        </div>
        {services === null ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
            Loading
          </div>
        ) : (
          <ul>
            {services.map((s) => (
              <li key={s.key} className="flex items-center gap-3 px-4 py-2 border-b border-border/30 text-xs" data-testid="ai-service-row">
                <span className="w-48 font-medium">{s.name}</span>
                <span className="w-32 text-muted-foreground">{s.vendor ?? ''}</span>
                <span className="flex-1 font-mono text-[11px] text-muted-foreground truncate">{s.domains.join(', ')}</span>
                <Badge variant={s.approved ? 'success' : s.action === 'block' ? 'critical' : 'warning'} className="text-[9px]">
                  {s.approved ? 'approved' : s.action === 'block' ? 'unapproved, blocked' : 'unapproved'}
                </Badge>
                {canApprove && (
                  <Button size="sm" variant="glass" disabled={busyKey !== null} onClick={() => void toggle(s)}>
                    {busyKey === s.key ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                    {s.approved ? 'Unapprove' : 'Approve'}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </GlassCard>

      <ShadowAiBoundaryCard canApprove={canApprove} />
    </div>
  );
}
